import { useState, useEffect, useContext } from "react";
import { AuthContext, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { 
  FileText, 
  Save, 
  RotateCcw, 
  ChevronDown, 
  ChevronRight,
  Layout,
  Home,
  LogIn,
  UserPlus,
  Users,
  Briefcase,
  Globe
} from "lucide-react";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";

const PAGE_ICONS = {
  landing: Home,
  login: LogIn,
  register: UserPlus,
  user_dashboard: Users,
  interpreter_dashboard: Briefcase,
  global: Globe,
};

const PAGE_LABELS = {
  landing: "Landing Page",
  login: "Login Page",
  register: "Register Page",
  user_dashboard: "User Dashboard",
  interpreter_dashboard: "Interpreter Dashboard",
  global: "Global (Footer, etc.)",
};

const ContentManager = () => {
  const { token } = useContext(AuthContext);
  const [content, setContent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editedContent, setEditedContent] = useState({});
  const [openPages, setOpenPages] = useState(["landing"]);

  // Use XMLHttpRequest to avoid body stream issues
  const apiRequest = (url, options = {}) => {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open(options.method || 'GET', url, true);
      if (options.headers) {
        Object.keys(options.headers).forEach(key => {
          xhr.setRequestHeader(key, options.headers[key]);
        });
      }
      xhr.timeout = 15000;
      xhr.onload = () => {
        try {
          const data = JSON.parse(xhr.responseText);
          resolve({ ok: xhr.status >= 200 && xhr.status < 300, status: xhr.status, data });
        } catch (e) {
          resolve({ ok: false, status: xhr.status, data: { detail: 'Invalid server response' } });
        }
      };
      xhr.onerror = () => reject(new Error('Network error'));
      xhr.ontimeout = () => reject(new Error('Request timed out'));
      xhr.send(options.body || null);
    });
  };

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const { ok, data } = await apiRequest(`${API}/content`);
      if (ok) {
        setContent(data);
        // Initialize edited content
        const edited = {};
        data.forEach(item => {
          edited[item.content_id] = item.content;
        });
        setEditedContent(edited);
      }
    } catch (error) {
      toast.error("Failed to load content");
    } finally {
      setLoading(false);
    }
  };

  const handleContentChange = (contentId, newContent) => {
    setEditedContent(prev => ({
      ...prev,
      [contentId]: newContent
    }));
  };

  const hasChanges = (contentId) => {
    const original = content.find(c => c.content_id === contentId);
    return original && editedContent[contentId] !== original.content;
  };

  const saveContent = async (contentId) => {
    setSaving(true);
    try {
      const { ok, data } = await apiRequest(`${API}/admin/content/${contentId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ content: editedContent[contentId] }),
      });

      if (ok) {
        toast.success("Content saved!");
        // Update local state
        setContent(prev => prev.map(c => 
          c.content_id === contentId ? { ...c, content: editedContent[contentId] } : c
        ));
      } else {
        toast.error(data.detail || "Failed to save content");
      }
    } catch (error) {
      toast.error(error.message || "An error occurred");
    } finally {
      setSaving(false);
    }
  };

  const saveAllChanges = async () => {
    const changes = content
      .filter(c => hasChanges(c.content_id))
      .map(c => ({ content_id: c.content_id, content: editedContent[c.content_id] }));

    if (changes.length === 0) {
      toast.info("No changes to save");
      return;
    }

    setSaving(true);
    try {
      const { ok, data } = await apiRequest(`${API}/admin/content/bulk`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ contents: changes }),
      });

      if (ok) {
        toast.success(`Saved ${data.updated_count} changes!`);
        fetchContent(); // Refresh
      } else {
        toast.error(data.detail || "Failed to save changes");
      }
    } catch (error) {
      toast.error(error.message || "An error occurred");
    } finally {
      setSaving(false);
    }
  };

  const resetToDefaults = async () => {
    if (!confirm("Are you sure you want to reset all content to defaults? This cannot be undone.")) {
      return;
    }

    setSaving(true);
    try {
      const { ok } = await apiRequest(`${API}/admin/content/reset`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (ok) {
        toast.success("Content reset to defaults!");
        fetchContent();
      } else {
        toast.error("Failed to reset content");
      }
    } catch (error) {
      toast.error(error.message || "An error occurred");
    } finally {
      setSaving(false);
    }
  };

  const togglePage = (page) => {
    setOpenPages(prev => 
      prev.includes(page) 
        ? prev.filter(p => p !== page)
        : [...prev, page]
    );
  };

  // Group content by page
  const groupedContent = content.reduce((acc, item) => {
    if (!acc[item.page]) {
      acc[item.page] = [];
    }
    acc[item.page].push(item);
    return acc;
  }, {});

  const totalChanges = content.filter(c => hasChanges(c.content_id)).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Site Content Manager
          </h2>
          <p className="text-sm text-slate-600 mt-1">
            Edit text content across all pages of the site
          </p>
        </div>
        <div className="flex gap-3">
          <Button
            data-testid="reset-content-btn"
            onClick={resetToDefaults}
            variant="outline"
            disabled={saving}
            className="text-red-600 border-red-300 hover:bg-red-50"
          >
            <RotateCcw className="w-4 h-4 mr-2" />
            Reset to Defaults
          </Button>
          <Button
            data-testid="save-all-btn"
            onClick={saveAllChanges}
            disabled={saving || totalChanges === 0}
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            <Save className="w-4 h-4 mr-2" />
            Save All Changes {totalChanges > 0 && `(${totalChanges})`}
          </Button>
        </div>
      </div>

      {/* Content Sections */}
      <div className="space-y-4">
        {Object.entries(groupedContent).map(([page, items]) => {
          const Icon = PAGE_ICONS[page] || Layout;
          const isOpen = openPages.includes(page);
          const pageChanges = items.filter(c => hasChanges(c.content_id)).length;

          return (
            <Collapsible key={page} open={isOpen} onOpenChange={() => togglePage(page)}>
              <CollapsibleTrigger asChild>
                <div 
                  data-testid={`page-section-${page}`}
                  className="w-full flex items-center justify-between p-4 bg-white border border-slate-200 rounded-lg cursor-pointer hover:bg-slate-50 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    {isOpen ? (
                      <ChevronDown className="w-4 h-4 text-slate-400" />
                    ) : (
                      <ChevronRight className="w-4 h-4 text-slate-400" />
                    )}
                    <Icon className="w-5 h-5 text-slate-600" />
                    <span className="font-medium text-slate-900">
                      {PAGE_LABELS[page] || page}
                    </span>
                    <span className="text-sm text-slate-500">
                      ({items.length} items)
                    </span>
                    {pageChanges > 0 && (
                      <span className="bg-amber-100 text-amber-700 text-xs px-2 py-0.5 rounded-full">
                        {pageChanges} unsaved
                      </span>
                    )}
                  </div>
                </div>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div className="mt-2 ml-4 border-l-2 border-slate-200 pl-4 space-y-3">
                  {items.map((item) => (
                    <div 
                      key={item.content_id}
                      className={`bg-white border rounded-lg p-4 ${
                        hasChanges(item.content_id) ? 'border-amber-300 bg-amber-50/50' : 'border-slate-200'
                      }`}
                    >
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <Label className="text-sm font-medium text-slate-700">
                            {item.section.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                          </Label>
                          <span className="ml-2 text-xs text-slate-400 capitalize">
                            ({item.content_type})
                          </span>
                        </div>
                        {hasChanges(item.content_id) && (
                          <Button
                            data-testid={`save-${item.content_id}`}
                            onClick={() => saveContent(item.content_id)}
                            disabled={saving}
                            size="sm"
                            className="bg-emerald-600 hover:bg-emerald-700 text-white"
                          >
                            <Save className="w-3 h-3 mr-1" />
                            Save
                          </Button>
                        )}
                      </div>
                      {item.content_type === "paragraph" || item.content.length > 100 ? (
                        <Textarea
                          data-testid={`content-${item.content_id}`}
                          value={editedContent[item.content_id] || ""}
                          onChange={(e) => handleContentChange(item.content_id, e.target.value)}
                          className="min-h-[80px] text-sm"
                          placeholder="Enter content..."
                        />
                      ) : (
                        <Input
                          data-testid={`content-${item.content_id}`}
                          value={editedContent[item.content_id] || ""}
                          onChange={(e) => handleContentChange(item.content_id, e.target.value)}
                          className="text-sm"
                          placeholder="Enter content..."
                        />
                      )}
                    </div>
                  ))}
                </div>
              </CollapsibleContent>
            </Collapsible>
          );
        })}
      </div>

      {content.length === 0 && (
        <div className="text-center py-12 text-slate-500">
          No content found. Click "Reset to Defaults" to initialize site content.
        </div>
      )}
    </div>
  );
};

export default ContentManager;
